import { ReactElement } from 'react'
import { Box, Paragraph } from '@alto-avios/alto-ui'
import { useTranslations } from 'next-intl'
import { ChildrenAgePicker } from '~/components/shared/ChildrenAgePicker'
import { UNSELECTED_CHILD_AGE } from '~/components/shared/ChildrenAgePicker/utils'
import TravelerQuantityPicker from '~/components/shared/TravelerQuantityPicker/TravelerQuantityPicker'
import { TravelerRoomProps } from '../TravelerRoomPicker.types'
import {
  REMOVE_ROOM,
  SET_TRAVELERS,
  MAX_TRAVELERS_PER_BOOKING,
  MAX_TRAVELERS_PER_ROOM,
  MAX_CHILDREN_PER_ROOM,
  MAX_ADULTS_PER_ROOM,
  SET_CHILD_AGE,
} from '../TravelerRoomPicker.constants'
import styles from './TravelerRoom.module.scss'

const TravelerRoom = ({
  room,
  dispatch,
  totalTravelers,
  childAgeErrors,
}: TravelerRoomProps): ReactElement<React.PropsWithChildren> => {
  const t = useTranslations('common')

  const AdultsLabel = (
    <Paragraph size="md" foregroundColor="primary">
      {t('labels.adults')}
    </Paragraph>
  )
  const ChildrenLabel = (
    <Box display="flex" flexDirection="column">
      <Paragraph size="md" foregroundColor="primary">
        {t('labels.children')}
      </Paragraph>
      <Paragraph size="sm">{t('labels.children_age_range')}</Paragraph>
    </Box>
  )

  const { roomNumber, adults, children } = room

  const travelers = {
    adult: {
      limits: {
        minCount: 1,
        maxCount: totalTravelers < MAX_TRAVELERS_PER_BOOKING ? MAX_ADULTS_PER_ROOM : 1,
      },
      quantity: adults,
      label: AdultsLabel,
      isVisible: true,
    },
    child: {
      limits: {
        minCount: 0,
        maxCount: totalTravelers < MAX_TRAVELERS_PER_BOOKING ? MAX_CHILDREN_PER_ROOM : 0,
      },
      quantity: children,
      label: ChildrenLabel,
      isVisible: true,
    },
    infant: { isVisible: false },
    senior: { isVisible: false },
  }

  return (
    <div
      data-testid="traveller-room-section"
      tabIndex={-1}
      role="listitem"
      aria-label="traveler room"
    >
      <Box paddingTop="sm" paddingBottom="sm" gap="xs" alignItems="center">
        <Paragraph size="sm">{`${t('labels.room')} ${roomNumber}`}</Paragraph>
        {roomNumber !== 1 && (
          <button
            type="button"
            className={styles.removeButton}
            onClick={() => dispatch({ type: REMOVE_ROOM, payload: { roomNumber } })}
          >
            {t('actions.remove')}
          </button>
        )}
      </Box>
      <TravelerQuantityPicker
        data-testid="traveler-quantity-picker"
        maxTravelers={MAX_TRAVELERS_PER_BOOKING}
        maxTravelersPerRoom={MAX_TRAVELERS_PER_ROOM}
        totalTravelerCount={totalTravelers}
        travelers={travelers}
        onChange={({ adult: { quantity: adultQuantity }, child: { quantity: childQuantity } }) =>
          dispatch({
            type: SET_TRAVELERS,
            payload: {
              roomNumber,
              adults: adultQuantity,
              children: childQuantity,
            },
          })
        }
      />
      <ChildrenAgePicker
        childrenAges={room.childrenAges}
        errors={childAgeErrors}
        unselectedAge={UNSELECTED_CHILD_AGE}
        handleSelect={({ child, age }) => {
          dispatch({ type: SET_CHILD_AGE, payload: { roomNumber, child, age } })
        }}
      />
    </div>
  )
}

export default TravelerRoom
